<template>
    <v-bottom-sheet v-model="dialog" v-stack="{ open: dialog, close: hide }" :max-width="560" @input="onSheetInput"
        content-class="pfadd-dialog">
        <div class="pfadd-sheet">
            <div class="pfadd-header">
                <v-btn icon small class="pfadd-back" @click="goBack">
                    <v-icon size="18">mdi-chevron-left</v-icon>
                </v-btn>
                <span class="pfadd-title">{{ currentTitle }}</span>
            </div>

            <div class="pfadd-body">
                <v-carousel :show-arrows="false" hide-delimiters touchless v-model="carousel" height="auto">
                    <v-carousel-item>
                        <div class="pfadd-filters">
                            <kimsa-combobox :placeholder="$t('exercises.pick_one_category')" dense
                                :items="categoriesOptions" @change="categories = $event" @clear="onClearCategories"
                                placeholder-class item-text="label" ref="categories" />
                            <kimsa-combobox :placeholder="$t('exercises.pick_type')" dense :readonly="!categories"
                                :items="typesOptions" item-text="label" @change="types = $event" @clear="onClearTypes"
                                placeholder-class multiple-with-chips :show-close-button="!!categories" ref="types" />
                        </div>

                        <kimsa-text-field class="pfadd-search-field" :placeholder="$t('common.search')" :value="search"
                            @change="search = $event" @click:append="sendTextQuery" prepend-inner-icon="mdi-magnify"
                            dense clearable no-elevation />

                        <div ref="listWrap" class="pfadd-list-wrap">
                            <div v-if="exercisesOptions.length === 0" class="pfadd-empty">
                                <small v-if="firstSearch">{{ $t('exercises.write_to_search_or_pick') }}</small>
                                <small v-else>{{ $t('common.not_found') }}</small>
                            </div>

                            <v-virtual-scroll v-else class="pfadd-scroll" :bench="8" :items="exercisesOptions"
                                :height="listHeight" width="100%" item-height="72">
                                <template v-slot:default="{ item }">
                                    <add-exercise-card :exercise="item" :added="isExerciseAdded(item.objectID)"
                                        @add="addExercise($event)" :one-exercise="oneExercise" />
                                </template>
                            </v-virtual-scroll>
                        </div>
                    </v-carousel-item>
                    <v-carousel-item>
                        <exercise-creator ref="creator" @success="onCreatorSuccess" @cancel="onCreatorCancel" bottom-btn
                            hide-title />
                    </v-carousel-item>
                </v-carousel>
            </div>

            <div v-if="carousel === 0" ref="footer" class="pfadd-footer">
                <v-btn v-if="!oneExercise" block depressed color="primary" class="black--text pfadd-cta"
                    :disabled="!isValid" @click="carousel === setExerciseStep ? setExercises() : continueStepper()">
                    {{ continueLabel }}
                </v-btn>
                <v-btn v-if="carousel <= setExerciseStep" text block class="pfadd-new" @click="carousel = 1">
                    <v-icon left size="16">mdi-plus</v-icon>{{ $t('common.create') }} {{ $t('common.new') }}
                    {{ $tc('exercises.exercises', 1) }}
                </v-btn>
            </div>
        </div>
    </v-bottom-sheet>
</template>

<script>

import AddExerciseCard from '@/components/coach/routines/AddExerciseCard'

import { mapState, mapActions } from 'vuex';

import { debounce } from '@/utils/Common'
import { searchExercises } from '@/utils/ExerciseLibrary'
import ExerciseCreator from "@/components/coach/routines/ExerciseCreator";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";

export default {
    props: {
        title: {
            type: String,
            default() {
                return this.$tc('common.blocks', 1)
            }
        },
        block: {
            type: [Object],
        },
        oneExercise: {
            type: Boolean,
            default: false,
        },
        defaultFilter: {
            type: [Object],
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        }
    },
    components: {
        KimsaTextField,
        KimsaCombobox,
        ExerciseCreator,
        AddExerciseCard,
    },
    data() {
        return {
            firstSearch: true,
            loadingSearch: false,
            dialog: false,
            carousel: 0,
            lastCarousel: 0,
            setExerciseStep: 0,
            search: '',
            categories: [],
            types: [],
            exercises: [],
            exercisesOptions: [],
            type: '',
            listHeight: 260,
            viewportBound: false,
        }
    },
    computed: {
        ...mapState(['categoriesOptions']),
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        continueLabel(vm = this) {
            return vm.carousel === vm.setExerciseStep ?
                vm.$t('exercises.add_number_exercises', { number: vm.exercises.length }) :
                vm.$t('common.continue')
        },
        isValid() {
            const vm = this
            if (vm.carousel === 1 && vm.types.length === 0) return false
            if (vm.carousel === vm.setExerciseStep && vm.exercises.length === 0) return false
            return true
        },
        typesOptions(vm = this) {
            if (vm.categories === undefined || vm.categories.length === 0) return []
            return vm.categories.types.map(type => ({ name: type, value: type, label: vm.typeLabel(type) })).sort((a, b) => a.label.localeCompare(b.label))
        },
        currentTitle(vm = this) {
            if (vm.carousel === 0) return `${vm.title}: ${vm.$t('exercises.add_exercises')}`
            return `${vm.$t('common.create')} ${vm.$t('common.new')} ${vm.$tc('exercises.exercises', 1)}`
        }
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        onTypesChange(types) {
            const vm = this
            vm.types = types
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return vm.hide()
            if (vm.carousel > vm.setExerciseStep) return vm.$refs.creator.goBack()
            if (vm.lastCarousel !== null) return vm.carousel = vm.lastCarousel
            vm.carousel--
        },
        onCreatorCancel() {
            const vm = this
            if (vm.lastCarousel !== null) return vm.carousel = vm.lastCarousel
            vm.carousel--
        },
        onCreatorSuccess(exercise) {
            const vm = this
            console.log('exercise CREATED', exercise)
            vm.addExercise(exercise)
            let existingExerciseIndex = vm.exercisesOptions.findIndex(ex => ex.objectID === exercise.objectID)
            if (existingExerciseIndex === -1) vm.exercisesOptions.push(exercise)
            vm.carousel = vm.setExerciseStep
            vm.$refs.creator.reset()
        },
        show() {
            const vm = this
            vm.dialog = true
            vm.onBackPress(vm.goBack)
            vm.bindViewport()
            vm.$nextTick(() => {
                if (vm.defaultFilter) vm.setDefaultFilter()
                vm.computeListHeight()
            })
            setTimeout(vm.computeListHeight, 350)
        },
        setKeyboardInset() {
            const viewport = window.visualViewport
            const inset = viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0
            document.documentElement.style.setProperty('--pfadd-kb', `${Math.round(inset)}px`)
        },
        computeListHeight() {
            const vm = this
            vm.setKeyboardInset()
            const wrap = vm.$refs.listWrap
            if (!wrap) return
            const viewport = window.visualViewport
            const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight
            const footer = vm.$refs.footer ? vm.$refs.footer.offsetHeight : 0
            const available = bottom - wrap.getBoundingClientRect().top - footer - 24
            vm.listHeight = Math.max(160, Math.floor(available))
        },
        onViewportChange() {
            const vm = this
            vm.$nextTick(vm.computeListHeight)
        },
        bindViewport() {
            const vm = this
            if (vm.viewportBound) return
            vm.viewportBound = true
            window.addEventListener('resize', vm.onViewportChange)
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', vm.onViewportChange)
                window.visualViewport.addEventListener('scroll', vm.onViewportChange)
            }
        },
        unbindViewport() {
            const vm = this
            if (!vm.viewportBound) return
            vm.viewportBound = false
            window.removeEventListener('resize', vm.onViewportChange)
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', vm.onViewportChange)
                window.visualViewport.removeEventListener('scroll', vm.onViewportChange)
            }
            document.documentElement.style.setProperty('--pfadd-kb', '0px')
        },
        setDefaultFilter() {
            const vm = this
            let category = vm.categoriesOptions.find(category => category.name === vm.defaultFilter.categories)
            let types = (vm.defaultFilter.types || []).map(type => ({ name: type, value: type, label: vm.typeLabel(type) }))
            vm.$refs.categories.model = category
            vm.$refs.types.model = types
        },
        hide() {
            const vm = this
            vm.reset()
            vm.dialog = false
            vm.clearOnBack()
            vm.unbindViewport()
        },
        onSheetInput(val) {
            if (!val && this.dialog) this.hide()
        },
        reset() {
            const vm = this
            vm.carousel = 0
            vm.search = ''
            vm.categories = []
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.categories.model = ''
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
            vm.exercises = []
            vm.exercisesOptions = []
        },
        continueStepper() {
            const vm = this
            return vm.carousel++
        },
        setExercises() {
            const vm = this
            vm.$emit('change', vm.exercises.slice())
            vm.hide()
            vm.reset()
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 300),
        sendTextQuery() {
            const vm = this
            if (!vm.search) return
            vm.loadExercises()
            vm.carousel = vm.setExerciseStep
        },
        async loadExercises() {
            let vm = this
            if (!vm.account) return
            vm.exercisesOptions = []
            vm.firstSearch = false
            vm.loadingSearch = true

            const companyId = (vm.account.accountType === 'coach_company' || vm.isCompany)
                ? (vm.account?.coachSubscription?.companyId || vm.currentUser.uid)
                : null

            try {
                vm.exercisesOptions = await searchExercises({
                    coachId: vm.currentUser.uid,
                    companyId,
                    query: vm.search,
                    types: vm.types,
                })
            } catch (err) {
                console.log(err)
            }

            vm.loadingSearch = false
        },
        addExercise(exercise) {
            const vm = this

            if (vm.oneExercise) {
                vm.exercises.push(exercise)
                return vm.setExercises()
            }

            let added = false
            vm.exercises.forEach((ex, i) => {
                if (ex.objectID === exercise.objectID) {
                    vm.exercises.splice(i, 1)
                    added = true
                }
            })
            if (!added) vm.exercises.push(exercise)
        },
        isExerciseAdded(id) {
            const vm = this
            let added = false
            vm.exercises.forEach(ex => {
                if (ex.objectID === id) added = true
            })
            return added
        },
        onClearCategories() {
            const vm = this
            vm.categories = []
            vm.types = []
            vm.debouncedLoadExercises()
        },
        onClearTypes() {
            const vm = this
            vm.types = []
        },
    },
    beforeDestroy() {
        this.unbindViewport()
    },
    watch: {
        carousel(val, oldVal) {
            const vm = this
            vm.lastCarousel = !isNaN(Number(oldVal)) && oldVal < val ? Number(oldVal) : null
            vm.$nextTick(vm.computeListHeight)
        },
        exercisesOptions() {
            this.$nextTick(this.computeListHeight)
        },
        types(val) {
            const vm = this
            if (val && val.length > 0) {
                vm.debouncedLoadExercises()
            } else {
                vm.exercises = []
                vm.exercisesOptions = []
            }
        },
        dialog(val) {
            if (!val) this.$emit('close')
        },


        search() {
            this.debouncedLoadExercises()
        },
        categories() {
            const vm = this
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
        },
    }
}

</script>

<style>
.v-dialog__content.pfadd-dialog {
    height: calc(100% - var(--pfadd-kb, 0px));
    transition: height 0.15s ease-out;
}
</style>

<style scoped>
.pfadd-sheet {
    background: var(--v-surface-base);
    border-top: 1px solid var(--v-outline-base);
    border-radius: 20px 20px 0 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
    width: 100%;
}

.pfadd-list-wrap {
    min-height: 0;
}

.pfadd-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.pfadd-back {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    flex-shrink: 0;
}

.pfadd-back .v-icon {
    color: inherit !important;
}

.pfadd-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pfadd-body {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px;
}

@media (max-width: 959px) {
    .pfadd-body::-webkit-scrollbar {
        display: none;
    }
}

.pfadd-filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 10px;
}

.pfadd-search-field {
    margin-bottom: 10px;
}

.pfadd-empty {
    text-align: center;
    padding: 28px 0;
    color: var(--v-txtMuted-base);
}

.pfadd-footer {
    padding: 12px 16px calc(12px + var(--safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--v-outline-base);
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: var(--v-surface-base);
}

.pfadd-cta {
    height: 48px !important;
    border-radius: 12px !important;
    font-weight: 700;
    text-transform: none !important;
    letter-spacing: normal !important;
}

.pfadd-new {
    height: 48px !important;
    border: 1px solid var(--v-outline-base) !important;
    border-radius: 12px !important;
    color: var(--v-txtPrimary-base) !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    font-weight: 600;
}
</style>